'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel from 'embla-carousel-react';
import React, { ViewTransition } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { EmblaOptionsType } from 'embla-carousel';
import { CarouselContainer } from '@/components/Carousel/CarouselContainer';
import { CarouselPaginationContainer } from '@/components/Carousel/CarouselPaginationContainer';
import { CarouselRoot } from '@/components/Carousel/CarouselRoot';
import { CarouselSlide } from '@/components/Carousel/CarouselSlide';
import { CarouselViewport } from '@/components/Carousel/CarouselViewport';
import type { CmsImageProps } from '@/components/CmsImage';
import { CmsImage } from '@/components/CmsImage';
import type { CmsImageData } from '@/services/cms/models/CmsImageData';
import { CarouselNextButton, CarouselPrevButton, useCarouselNavigationButtons } from './CarouselArrowButtons';
import { CarouselNavigationContainer } from './CarouselNavigationContainer';
import { CarouselPaginationButton, useCarouselPaginationButtons } from './CarouselPaginationButton';
type CarouselProps = {
  images: CmsImageData[];
  options?: EmblaOptionsType;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  showPagination?: boolean;
  showNavigation?: boolean;
  transitionName?: string;
  sizes?: CmsImageProps['sizes'];
};
export function Carousel(t0) {
  const $ = _c(29);
  const {
    images,
    options,
    sx,
    children,
    showPagination: t1,
    showNavigation: t2,
    transitionName,
    sizes
  } = t0;
  const showPagination = t1 === undefined ? true : t1;
  const showNavigation = t2 === undefined ? false : t2;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const {
    selectedIndex,
    scrollSnaps,
    onPaginationButtonClick
  } = useCarouselPaginationButtons(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = useCarouselNavigationButtons(emblaApi);
  let t3;
  if ($[0] !== images || $[1] !== sizes || $[2] !== transitionName) {
    let t4;
    if ($[4] !== sizes || $[5] !== transitionName) {
      t4 = (slide, index) => {
        const image = <CmsImage {...slide} sizes={sizes} priority={index === 0} style={{
          objectFit: "cover",
          width: "100%"
        }} />;
        const isTransitionEnabled = index === 0 && transitionName;
        return <CarouselSlide className="carousel-slide" key={`slide-${slide.id}`}>{isTransitionEnabled ? <ViewTransition name={transitionName}>{image}</ViewTransition> : image}</CarouselSlide>;
      };
      $[4] = sizes;
      $[5] = transitionName;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = images.map(t4);
    $[0] = images;
    $[1] = sizes;
    $[2] = transitionName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <CarouselContainer>{t3}</CarouselContainer>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== children || $[10] !== emblaRef || $[11] !== t4) {
    t5 = <CarouselViewport ref={emblaRef}>{t4}{children}</CarouselViewport>;
    $[9] = children;
    $[10] = emblaRef;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== nextBtnDisabled || $[14] !== onNextButtonClick || $[15] !== onPrevButtonClick || $[16] !== prevBtnDisabled || $[17] !== showNavigation) {
    t6 = showNavigation && <CarouselNavigationContainer><CarouselPrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} aria-label="Previous slide" /><CarouselNextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} aria-label="Next slide" /></CarouselNavigationContainer>;
    $[13] = nextBtnDisabled;
    $[14] = onNextButtonClick;
    $[15] = onPrevButtonClick;
    $[16] = prevBtnDisabled;
    $[17] = showNavigation;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== onPaginationButtonClick || $[20] !== scrollSnaps || $[21] !== selectedIndex || $[22] !== showPagination) {
    t7 = showPagination && <CarouselPaginationContainer>{scrollSnaps.map((_, index_0) => <CarouselPaginationButton key={index_0} onClick={() => onPaginationButtonClick(index_0)} active={index_0 === selectedIndex} aria-label={`Slide show page ${index_0 + 1}`} />)}</CarouselPaginationContainer>;
    $[19] = onPaginationButtonClick;
    $[20] = scrollSnaps;
    $[21] = selectedIndex;
    $[22] = showPagination;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== sx || $[25] !== t5 || $[26] !== t6 || $[27] !== t7) {
    t8 = <CarouselRoot sx={sx}>{t5}{t6}{t7}</CarouselRoot>;
    $[24] = sx;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}